import type { UpfileV2Commands } from "@nijiurachan/js/components";
import type { UpfileUiHintFlags } from "@nijiurachan/js/pure";
import { BsPalette } from "react-icons/bs";
import { FiClipboard, FiPaperclip } from "react-icons/fi";

interface Props {
  hint: UpfileUiHintFlags | undefined;
  host: UpfileV2Commands;
  attachmentsDisabled?: boolean;
  surface?: "desktop" | "mobile";
  trailingAction?: UpfileTrailingAction;
}

export type UpfileTrailingAction = {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  isDisabled?: boolean;
  isExpanded?: boolean;
  controls?: string;
};

export const UpfileActions: React.FunctionComponent<Props> = ({
  hint,
  host,
  attachmentsDisabled = false,
  surface = "mobile",
  trailingAction,
}: Props) => {
  const buttonClass =
    "upfile-action-button flex w-[5.25rem] items-center whitespace-nowrap px-3 py-2 rounded-lg border border-border bg-muted text-foreground text-sm transition-colors enabled:hover:bg-background disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-primary";
  const buttonContentClass =
    surface === "desktop"
      ? "flex-row justify-center gap-1.5"
      : "flex-col justify-center gap-1";

  return (
    <fieldset className="m-0 flex min-w-0 flex-col gap-2 border-0 p-0">
      <legend className="sr-only">
        {trailingAction ? "添付とアンケート操作" : "画像操作"}
      </legend>
      {hint?.showAllowImageLabel && (
        <aside className="text-xs text-muted-foreground">
          画像添付は許可されていません（お絵描きは可能）
        </aside>
      )}
      <menu className="m-0 flex list-none flex-wrap gap-2 p-0">
        <li>
          <button
            type="button"
            className={`${buttonClass} ${buttonContentClass}`}
            onClick={(): void => host.clickFileattach()}
            disabled={attachmentsDisabled || !hint?.showUpfileButton}
          >
            <FiPaperclip aria-hidden="true" className="w-11 h-5" />
            <small className="text-[0.7rem] leading-none">ファイル</small>
          </button>
        </li>

        <li>
          <button
            type="button"
            className={`${buttonClass} ${buttonContentClass}`}
            onClick={(): void => host.clickPaint()}
            disabled={attachmentsDisabled || !hint?.showPaintButton}
          >
            <BsPalette aria-hidden="true" className="w-11 h-5" />
            <small className="text-[0.7rem] leading-none">お絵かき</small>
          </button>
        </li>

        <li>
          <button
            type="button"
            className={`${buttonClass} ${buttonContentClass}`}
            onClick={(): void => host.clickPaste()}
            disabled={attachmentsDisabled || !hint?.showPasteButton}
          >
            <FiClipboard aria-hidden="true" className="w-11 h-5" />
            <small className="text-[0.7rem] leading-none">貼り付け</small>
          </button>
        </li>
        {trailingAction && (
          <li>
            <button
              type="button"
              className={`${buttonClass} ${buttonContentClass}`}
              onClick={trailingAction.onClick}
              disabled={trailingAction.isDisabled}
              aria-expanded={trailingAction.isExpanded}
              aria-controls={trailingAction.controls}
            >
              {trailingAction.icon}
              <small className="text-[0.7rem] leading-none">
                {trailingAction.label}
              </small>
            </button>
          </li>
        )}
      </menu>
    </fieldset>
  );
};
